import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext, AccessContext } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumericInput } from '../components/ui/numeric-input';
import { Label } from '../components/ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, CreditCard, Receipt, Download, ShoppingCart, Banknote, HandCoins, Wallet, TrendingUp, TrendingDown, Lock } from 'lucide-react';

// Get current date in IST timezone
const getISTDate = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

// Get start of month in IST timezone
const getISTMonthStart = () => {
  const now = new Date();
  const istDate = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  istDate.setDate(1);
  return istDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const basePaymentMethods = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_acc', label: 'Bank Payment' },
  { value: 'phonepe', label: 'Phone Pe' },
  { value: 'paytm', label: 'Paytm' },
  { value: 'mobiquick', label: 'MobiQuick' },
  { value: 'company_tieup', label: 'Company Tie-up' },
  { value: 'prepay_otp', label: 'Prepay OTP' },
  { value: 'fleet_account', label: 'Fleet Account' },
  { value: 'other_upi', label: 'Other UPI Payment' },
  { value: 'coupon', label: 'Coupon' }
];

const expenseCategories = [
  'Salary', 'Rent', 'Chai Pani', 'Office Stationary', 'Petrol Expense',
  'Employee Reward', 'Customer Reward', 'Electrician', 'Mechanic',
  'Construction', 'Miscellaneous'
];

function QuickActions() {
  const { user } = useContext(AuthContext);
  const { accessSettings } = useContext(AccessContext);
  
  // Check report download permission
  const userRole = user?.role || 'viewer';
  const reportAccess = accessSettings?.report_access?.[userRole] || { can_view: true, can_download: false, months_allowed: 1 };
  const canDownload = reportAccess.can_download;
  
  const [customers, setCustomers] = useState([]);
  const [transactions, setTransactions] = useState([]); // Combined: sales, payments, cash requests
  const [expenses, setExpenses] = useState([]);
  const [expenseStats, setExpenseStats] = useState({ total_amount: 0, category_breakdown: {} });
  const [withdrawals, setWithdrawals] = useState([]);
  const [customerBalance, setCustomerBalance] = useState(null);
  const [customPaymentMethods, setCustomPaymentMethods] = useState([]);
  
  // Payment breakdown state
  const [paymentBreakdowns, setPaymentBreakdowns] = useState([]);
  const [breakdownDateRange, setBreakdownDateRange] = useState({
    start: getISTDate(),
    end: getISTDate()
  });
  
  const [expenseDialog, setExpenseDialog] = useState(false);
  const [withdrawalDialog, setWithdrawalDialog] = useState(false);
  const [transactionDialog, setTransactionDialog] = useState(false);
  const [transactionType, setTransactionType] = useState(null); // 'sale' | 'payment' | 'cash_request' | 'payment_breakdown'
  const [paymentDialogTab, setPaymentDialogTab] = useState('customer_payment'); // 'customer_payment' | 'day_breakdown'
  
  // Fetch prefill data when day_breakdown tab is selected
  useEffect(() => {
    if (paymentDialogTab === 'day_breakdown' && paymentBreakdownForm.date) {
      fetchPaymentBreakdownPrefill(paymentBreakdownForm.date);
    }
  }, [paymentDialogTab]);
  
  const [dateRange, setDateRange] = useState({
    start: getISTMonthStart(),
    end: getISTDate()
  });

  const [expenseForm, setExpenseForm] = useState({
    date: '',
    category: 'Miscellaneous',
    description: '',
    amount: 0,
    paid_to: '',
    payment_method: 'cash'
  });

  const [withdrawalForm, setWithdrawalForm] = useState({
    date: '',
    partner_name: '',
    amount: 0,
    description: '',
    payment_method: 'cash',
    transaction_type: 'withdrawal' // 'withdrawal' or 'investment'
  });

  // Sale form
  const [saleForm, setSaleForm] = useState({
    date: '',
    customer_id: '',
    fuel_type: 'MS',
    quantity: 0,
    unit_price: 0,
    discount: 0,
    is_credit: true,
    payment_method: 'cash'
  });

  // Customer Payment form
  const [customerPaymentForm, setCustomerPaymentForm] = useState({
    date: '',
    customer_id: '',
    amount: 0,
    payment_method: 'cash',
    description: ''
  });

  // Cash Request form
  const [cashRequestForm, setCashRequestForm] = useState({
    date: '',
    customer_id: '',
    amount: 0,
    payment_method: 'cash',
    customer_employee_name: '',
    witness_name: '',
    note: ''
  });

  // Payment Breakdown form
  const [paymentBreakdownForm, setPaymentBreakdownForm] = useState({
    date: '',
    cash: 0,
    credit_sales: 0,
    credit_collection: 0,
    paytm: 0,
    phonepe: 0,
    mobiquick: 0,
    other_upi: 0,
    card_payments: 0,
    bank_payments: 0,
    fleet_payments: 0,
    otp_prepayments: 0,
    ambulance_otp_credit_sales: 0,
    custom_methods: {}
  });

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  useEffect(() => {
    fetchPaymentBreakdowns();
  }, [breakdownDateRange]);

  useEffect(() => {
    fetchCustomPaymentMethods();
  }, []);

  const fetchData = async () => {
    try {
      const [cusRes, salesRes, paymentsRes, cashReqRes, expRes, withRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/customer-sales?start_date=${dateRange.start}&end_date=${dateRange.end}`),
        axios.get(`${API}/customer-payments?start_date=${dateRange.start}&end_date=${dateRange.end}`),
        axios.get(`${API}/customer-cash-requests?start_date=${dateRange.start}&end_date=${dateRange.end}`),
        axios.get(`${API}/expenses?start_date=${dateRange.start}&end_date=${dateRange.end}`),
        axios.get(`${API}/withdrawals?start_date=${dateRange.start}&end_date=${dateRange.end}`)
      ]);

      setCustomers(cusRes.data.customers || []);
      setExpenses(expRes.data.expenses || []);
      setExpenseStats(expRes.data.stats || { total_amount: 0, category_breakdown: {} });
      setWithdrawals(withRes.data.withdrawals || []);

      // Combine sales, payments, and cash requests
      const sales = (salesRes.data.sales || []).map(s => ({ ...s, type: 'sale' }));
      const payments = (paymentsRes.data.payments || []).map(p => ({ ...p, type: 'payment' }));
      const cashRequests = (cashReqRes.data.cash_requests || []).map(cr => ({ ...cr, type: 'cash_request' }));
      
      const combined = [...sales, ...payments, ...cashRequests].sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      setTransactions(combined);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const fetchCustomPaymentMethods = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setCustomPaymentMethods(res.data.custom_payment_methods || []);
    } catch (error) {
      console.error('Failed to fetch custom payment methods');
    }
  };

  const fetchPaymentBreakdowns = async () => {
    try {
      const res = await axios.get(`${API}/payment-breakdown?start_date=${breakdownDateRange.start}&end_date=${breakdownDateRange.end}`);
      setPaymentBreakdowns(res.data.breakdowns || []);
    } catch (error) {
      toast.error('Failed to load payment breakdowns');
    }
  };

  const fetchCustomerBalance = async (customerId) => {
    try {
      const res = await axios.get(`${API}/customer-balance/${customerId}`);
      setCustomerBalance(res.data);
    } catch (error) {
      setCustomerBalance(null);
    }
  };

  const fetchPaymentBreakdownPrefill = async (date) => {
    try {
      const res = await axios.get(`${API}/payment-breakdown/prefill?date=${date}`);
      setPaymentBreakdownForm(prev => ({
        ...prev,
        credit_sales: res.data.credit_sales,
        credit_collection: res.data.credit_collection
      }));
    } catch (error) {
      toast.error('Failed to load pre-fill data');
    }
  };

  // Handle Expense Submit
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseForm.date) {
      toast.error('Please select a date');
      return;
    }
    if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await axios.post(`${API}/expenses`, {
        date: new Date(expenseForm.date).toISOString(),
        category: expenseForm.category,
        description: expenseForm.description,
        amount: parseFloat(expenseForm.amount),
        paid_to: expenseForm.paid_to,
        payment_method: expenseForm.payment_method
      });
      toast.success('Expense recorded');
      setExpenseDialog(false);
      fetchData();
      setExpenseForm({
        date: '',
        category: 'Miscellaneous',
        description: '',
        amount: 0,
        paid_to: '',
        payment_method: 'cash'
      });
    } catch (error) {
      toast.error('Failed to record expense');
    }
  };

  // Handle Withdrawal Submit
  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();
    if (!withdrawalForm.date) {
      toast.error('Please select a date');
      return;
    }
    if (!withdrawalForm.amount || parseFloat(withdrawalForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!withdrawalForm.partner_name.trim()) {
      toast.error('Please enter partner name');
      return;
    }
    try {
      await axios.post(`${API}/withdrawals`, {
        date: new Date(withdrawalForm.date).toISOString(),
        partner_name: withdrawalForm.partner_name,
        amount: parseFloat(withdrawalForm.amount),
        description: withdrawalForm.description,
        payment_method: withdrawalForm.payment_method,
        is_investment: withdrawalForm.transaction_type === 'investment'
      });
      toast.success(withdrawalForm.transaction_type === 'investment' ? 'Investment recorded' : 'Withdrawal recorded');
      setWithdrawalDialog(false);
      fetchData();
      setWithdrawalForm({
        date: '',
        partner_name: '',
        amount: 0,
        description: '',
        payment_method: 'cash',
        transaction_type: 'withdrawal'
      });
    } catch (error) {
      toast.error(withdrawalForm.transaction_type === 'investment' ? 'Failed to record investment' : 'Failed to record withdrawal');
    }
  };

  // Handle Sale Submit
  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    if (!saleForm.date) {
      toast.error('Please select a date');
      return;
    }
    if (!saleForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!saleForm.quantity || parseFloat(saleForm.quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    if (!saleForm.unit_price || parseFloat(saleForm.unit_price) <= 0) {
      toast.error('Please enter a valid unit price');
      return;
    }
    try {
      const payload = {
        date: new Date(saleForm.date).toISOString(),
        customer_id: saleForm.customer_id,
        fuel_type: saleForm.fuel_type,
        quantity: parseFloat(saleForm.quantity),
        unit_price: parseFloat(saleForm.unit_price),
        discount: parseFloat(saleForm.discount) || 0,
        is_credit: saleForm.is_credit,
        payment_method: saleForm.is_credit ? null : saleForm.payment_method
      };
      
      await axios.post(`${API}/customer-sales`, payload);
      toast.success(saleForm.is_credit ? 'Credit sale recorded' : 'Paid sale recorded');
      closeTransactionDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to record sale');
    }
  };

  // Handle Customer Payment Submit
  const handleCustomerPaymentSubmit = async (e) => {
    e.preventDefault();
    if (!customerPaymentForm.date) {
      toast.error('Please select a date');
      return;
    }
    if (!customerPaymentForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!customerPaymentForm.amount || parseFloat(customerPaymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await axios.post(`${API}/customer-payments`, {
        date: new Date(customerPaymentForm.date).toISOString(),
        customer_id: customerPaymentForm.customer_id,
        amount: parseFloat(customerPaymentForm.amount),
        payment_method: customerPaymentForm.payment_method,
        description: customerPaymentForm.description
      });
      toast.success('Payment recorded');
      closeTransactionDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  // Handle Cash Request Submit
  const handleCashRequestSubmit = async (e) => {
    e.preventDefault();
    if (!cashRequestForm.date) {
      toast.error('Please select a date');
      return;
    }
    if (!cashRequestForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!cashRequestForm.amount || parseFloat(cashRequestForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!cashRequestForm.customer_employee_name.trim()) {
      toast.error('Please enter customer employee name');
      return;
    }
    if (!cashRequestForm.witness_name.trim()) {
      toast.error('Please enter witness name');
      return;
    }
    try {
      await axios.post(`${API}/customer-cash-requests`, {
        date: new Date(cashRequestForm.date).toISOString(),
        customer_id: cashRequestForm.customer_id,
        amount: parseFloat(cashRequestForm.amount),
        payment_method: cashRequestForm.payment_method,
        customer_employee_name: cashRequestForm.customer_employee_name,
        witness_name: cashRequestForm.witness_name,
        note: cashRequestForm.note || null
      });
      toast.success('Cash request recorded & notification sent');
      closeTransactionDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to record cash request');
    }
  };

  // Handle Payment Breakdown Submit
  const handlePaymentBreakdownSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentBreakdownForm.date) {
      toast.error('Please select a date');
      return;
    }
    
    const daysTotalCollection = 
      parseFloat(paymentBreakdownForm.cash) +
      parseFloat(paymentBreakdownForm.credit_sales) +
      parseFloat(paymentBreakdownForm.credit_collection) +
      parseFloat(paymentBreakdownForm.paytm) +
      parseFloat(paymentBreakdownForm.phonepe) +
      parseFloat(paymentBreakdownForm.mobiquick) +
      parseFloat(paymentBreakdownForm.other_upi) +
      parseFloat(paymentBreakdownForm.card_payments) +
      parseFloat(paymentBreakdownForm.bank_payments) +
      parseFloat(paymentBreakdownForm.fleet_payments) +
      parseFloat(paymentBreakdownForm.otp_prepayments) +
      parseFloat(paymentBreakdownForm.ambulance_otp_credit_sales) +
      Object.values(paymentBreakdownForm.custom_methods).reduce((sum, val) => sum + parseFloat(val || 0), 0);

    if (daysTotalCollection <= 0) {
      toast.error('Please enter at least one payment amount');
      return;
    }

    try {
      const res = await axios.post(`${API}/payment-breakdown`, {
        date: new Date(paymentBreakdownForm.date).toISOString(),
        cash: parseFloat(paymentBreakdownForm.cash) || 0,
        credit_sales: parseFloat(paymentBreakdownForm.credit_sales) || 0,
        credit_collection: parseFloat(paymentBreakdownForm.credit_collection) || 0,
        paytm: parseFloat(paymentBreakdownForm.paytm) || 0,
        phonepe: parseFloat(paymentBreakdownForm.phonepe) || 0,
        mobiquick: parseFloat(paymentBreakdownForm.mobiquick) || 0,
        other_upi: parseFloat(paymentBreakdownForm.other_upi) || 0,
        card_payments: parseFloat(paymentBreakdownForm.card_payments) || 0,
        bank_payments: parseFloat(paymentBreakdownForm.bank_payments) || 0,
        fleet_payments: parseFloat(paymentBreakdownForm.fleet_payments) || 0,
        otp_prepayments: parseFloat(paymentBreakdownForm.otp_prepayments) || 0,
        ambulance_otp_credit_sales: parseFloat(paymentBreakdownForm.ambulance_otp_credit_sales) || 0,
        custom_methods: paymentBreakdownForm.custom_methods
      });
      
      toast.success(`Payment breakdown recorded. Days Total Collection: ₹${res.data.days_total_collection.toLocaleString('en-IN')}`);
      closeTransactionDialog();
      fetchPaymentBreakdowns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment breakdown');
    }
  };

  const closeTransactionDialog = () => {
    setTransactionDialog(false);
    setTransactionType(null);
    setPaymentDialogTab('customer_payment');
    setCustomerBalance(null);
    setSaleForm({
      date: '',
      customer_id: '',
      fuel_type: 'MS',
      quantity: 0,
      unit_price: 0,
      discount: 0,
      is_credit: true,
      payment_method: 'cash'
    });
    setCustomerPaymentForm({
      date: '',
      customer_id: '',
      amount: 0,
      payment_method: 'cash',
      description: ''
    });
    setCashRequestForm({
      date: '',
      customer_id: '',
      amount: 0,
      payment_method: 'cash',
      customer_employee_name: '',
      witness_name: '',
      note: ''
    });
    setPaymentBreakdownForm({
      date: '',
      cash: 0,
      credit_sales: 0,
      credit_collection: 0,
      paytm: 0,
      phonepe: 0,
      mobiquick: 0,
      other_upi: 0,
      card_payments: 0,
      bank_payments: 0,
      fleet_payments: 0,
      otp_prepayments: 0,
      ambulance_otp_credit_sales: 0,
      custom_methods: {}
    });
  };

  const getProjectedBalance = () => {
    if (!customerBalance) return null;
    const paymentAmount = parseFloat(customerPaymentForm.amount) || 0;
    return customerBalance.current_balance - paymentAmount;
  };

  const exportExpensePDF = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/expense-pdf?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'expense_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Expense report downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const downloadPaymentBreakdown = () => {
    if (paymentBreakdowns.length === 0) {
      toast.error('No data to download');
      return;
    }

    // Prepare CSV headers
    const headers = [
      'Date',
      'Cash',
      'Credit Sales',
      'Credit Collection',
      'Paytm',
      'PhonePe',
      'MobiQuick',
      'Other UPI',
      'Card Payments',
      'Bank Payments',
      'Fleet Payments',
      'OTP Prepayments',
      'Ambulance OTP Credit Sales',
      ...customPaymentMethods,
      'Days Total Collection',
      'Days Total'
    ];

    const rows = paymentBreakdowns.map(bd => {
      const customValues = customPaymentMethods.map(method => bd.custom_methods?.[method] || 0);
      return [
        new Date(bd.date).toLocaleDateString('en-IN'),
        bd.cash,
        bd.credit_sales,
        bd.credit_collection,
        bd.paytm,
        bd.phonepe,
        bd.mobiquick,
        bd.other_upi,
        bd.card_payments,
        bd.bank_payments,
        bd.fleet_payments,
        bd.otp_prepayments,
        bd.ambulance_otp_credit_sales,
        ...customValues,
        bd.days_total_collection,
        bd.days_total
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payment_breakdown_${breakdownDateRange.start}_${breakdownDateRange.end}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Payment breakdown downloaded');
  };

  // Get customer name from ID
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.firm_name : 'Unknown';
  };

  // Calculate Days Total Collection
  const calculateDaysTotalCollection = () => {
    return parseFloat(paymentBreakdownForm.cash || 0) +
      parseFloat(paymentBreakdownForm.credit_sales || 0) +
      parseFloat(paymentBreakdownForm.credit_collection || 0) +
      parseFloat(paymentBreakdownForm.paytm || 0) +
      parseFloat(paymentBreakdownForm.phonepe || 0) +
      parseFloat(paymentBreakdownForm.mobiquick || 0) +
      parseFloat(paymentBreakdownForm.other_upi || 0) +
      parseFloat(paymentBreakdownForm.card_payments || 0) +
      parseFloat(paymentBreakdownForm.bank_payments || 0) +
      parseFloat(paymentBreakdownForm.fleet_payments || 0) +
      parseFloat(paymentBreakdownForm.otp_prepayments || 0) +
      parseFloat(paymentBreakdownForm.ambulance_otp_credit_sales || 0) +
      Object.values(paymentBreakdownForm.custom_methods).reduce((sum, val) => sum + parseFloat(val || 0), 0);
  };

  // All payment methods (base + custom)
  const allPaymentMethods = [
    ...basePaymentMethods,
    ...customPaymentMethods.map(method => ({ value: method.toLowerCase().replace(/\s+/g, '_'), label: method }))
  ];

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="quick-actions-page">
        {/* Date Range Filter */}
        <Card className="p-3 sm:p-4 border border-border">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <label className="text-xs sm:text-sm font-medium whitespace-nowrap">From:</label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="flex-1 sm:w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs sm:text-sm font-medium whitespace-nowrap">To:</label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="flex-1 sm:w-40"
              />
            </div>
          </div>
        </Card>

        <Tabs defaultValue="sales" className="w-full">
          <TabsList className="flex flex-wrap w-full gap-1 h-auto p-1">
            <TabsTrigger value="sales" data-testid="sales-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
              <CreditCard className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
              Sales
            </TabsTrigger>
            <TabsTrigger value="expenses" data-testid="expenses-tab" className="flex-1 min-w-[80px] text-xs sm:text-sm">
              <Receipt className="w-4 h-4 mr-1 sm:mr-2 hidden sm:inline" />
              Expenses
            </TabsTrigger>
          </TabsList>

          {/* SALES TAB with SUB-TABS */}
          <TabsContent value="sales" className="mt-6">
            <Tabs defaultValue="customer_transactions">
              <TabsList>
                <TabsTrigger value="customer_transactions">Customer Transactions</TabsTrigger>
                <TabsTrigger value="payment_breakdown">Payment Breakdown</TabsTrigger>
              </TabsList>

              {/* CUSTOMER TRANSACTIONS SUB-TAB */}
              <TabsContent value="customer_transactions" className="mt-4">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-heading font-bold">Customer Transactions</h2>
                  <Dialog open={transactionDialog} onOpenChange={(open) => {
                    if (!open) closeTransactionDialog();
                    else setTransactionDialog(true);
                  }}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-transaction-button">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Transaction
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>
                          {!transactionType ? 'Select Transaction Type' : 
                           transactionType === 'sale' ? 'Add Sale' :
                           transactionType === 'payment' ? 'Add Payment' :
                           'Add Customer Cash Request'}
                        </DialogTitle>
                      </DialogHeader>
                      
                      {/* Transaction Type Selection */}
                      {!transactionType && (
                        <div className="grid md:grid-cols-3 gap-4 mt-4">
                          <button
                            onClick={() => setTransactionType('sale')}
                            className="p-6 border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-center"
                            data-testid="add-sale-option"
                          >
                            <ShoppingCart className="w-10 h-10 mx-auto mb-3 text-blue-600" />
                            <h3 className="font-semibold text-lg">Add Sale</h3>
                            <p className="text-sm text-muted mt-1">Record fuel sale (credit or paid)</p>
                          </button>
                          <button
                            onClick={() => setTransactionType('payment')}
                            className="p-6 border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-center"
                            data-testid="add-payment-option"
                          >
                            <Banknote className="w-10 h-10 mx-auto mb-3 text-green-600" />
                            <h3 className="font-semibold text-lg">Add Payment</h3>
                            <p className="text-sm text-muted mt-1">Record customer payment</p>
                          </button>
                          <button
                            onClick={() => setTransactionType('cash_request')}
                            className="p-6 border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-center"
                            data-testid="add-cash-request-option"
                          >
                            <HandCoins className="w-10 h-10 mx-auto mb-3 text-orange-600" />
                            <h3 className="font-semibold text-lg">Cash Request</h3>
                            <p className="text-sm text-muted mt-1">Cash given to customer</p>
                          </button>
                        </div>
                      )}
                      
                      {/* ADD SALE FORM (existing code - keeping it as is) */}
                      {transactionType === 'sale' && (
                        <form onSubmit={handleSaleSubmit} className="space-y-4 mt-4">
                          <div className="flex flex-col sm:flex-row gap-2 p-1 bg-secondary rounded-lg">
                            <button
                              type="button"
                              onClick={() => setSaleForm({ ...saleForm, is_credit: true })}
                              className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                                saleForm.is_credit ? 'bg-white shadow text-primary' : 'text-muted hover:text-foreground'
                              }`}
                            >
                              Credit Sale
                            </button>
                            <button
                              type="button"
                              onClick={() => setSaleForm({ ...saleForm, is_credit: false })}
                              className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                                !saleForm.is_credit ? 'bg-white shadow text-primary' : 'text-muted hover:text-foreground'
                              }`}
                            >
                              Paid Sale
                            </button>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Date *</label>
                              <Input type="date" value={saleForm.date} onChange={(e) => setSaleForm({ ...saleForm, date: e.target.value })} required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Customer *</label>
                              <Select value={saleForm.customer_id} onValueChange={(value) => {
                                setSaleForm({ ...saleForm, customer_id: value });
                                fetchCustomerBalance(value);
                              }}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select customer" />
                                </SelectTrigger>
                                <SelectContent>
                                  {customers.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.firm_name} - {c.customer_name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Fuel Type *</label>
                              <Select value={saleForm.fuel_type} onValueChange={(value) => setSaleForm({ ...saleForm, fuel_type: value })}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="MS">MS (Petrol)</SelectItem>
                                  <SelectItem value="HSD">HSD (Diesel)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Quantity (L) *</label>
                              <NumericInput step="0.01" value={saleForm.quantity} onChange={(e) => setSaleForm({ ...saleForm, quantity: e.target.value })} required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Unit Price (₹/L) *</label>
                              <NumericInput step="0.01" value={saleForm.unit_price} onChange={(e) => setSaleForm({ ...saleForm, unit_price: e.target.value })} required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Discount (₹)</label>
                              <NumericInput step="0.01" value={saleForm.discount} onChange={(e) => setSaleForm({ ...saleForm, discount: e.target.value })} />
                            </div>
                            
                            {!saleForm.is_credit && (
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-2">Payment Method *</label>
                                <Select value={saleForm.payment_method} onValueChange={(value) => setSaleForm({ ...saleForm, payment_method: value })}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {allPaymentMethods.map(pm => (
                                      <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          
                          <Card className="p-4 bg-secondary/50">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-muted">Gross Amount:</span>
                              <span className="font-semibold">₹{((parseFloat(saleForm.quantity) || 0) * (parseFloat(saleForm.unit_price) || 0)).toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between items-center mt-2">
                              <span className="text-sm text-muted">Discount:</span>
                              <span className="font-semibold text-green-600">- ₹{(parseFloat(saleForm.discount) || 0).toLocaleString('en-IN')}</span>
                            </div>
                            <div className="flex justify-between items-center mt-2 pt-2 border-t">
                              <span className="font-semibold">Net Amount:</span>
                              <span className="text-lg font-bold text-primary">₹{(((parseFloat(saleForm.quantity) || 0) * (parseFloat(saleForm.unit_price) || 0)) - (parseFloat(saleForm.discount) || 0)).toLocaleString('en-IN')}</span>
                            </div>
                          </Card>

                          {customerBalance && (
                            <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200">
                              <div className="text-sm">
                                <div className="flex justify-between mb-1">
                                  <span className="text-muted">Current Balance:</span>
                                  <span className="font-semibold">₹{customerBalance.current_balance.toLocaleString('en-IN')}</span>
                                </div>
                              </div>
                            </Card>
                          )}
                          
                          <div className="flex gap-2 justify-end pt-4">
                            <Button type="button" variant="outline" onClick={closeTransactionDialog}>Cancel</Button>
                            <Button type="submit">Record Sale</Button>
                          </div>
                        </form>
                      )}
                      
                      {/* ADD PAYMENT FORM - WITH TABS */}
                      {transactionType === 'payment' && (
                        <Tabs value={paymentDialogTab} onValueChange={setPaymentDialogTab} className="mt-4">
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="customer_payment">Add Customer Credit Payments</TabsTrigger>
                            <TabsTrigger value="day_breakdown">Add Day Payment Breakdown</TabsTrigger>
                          </TabsList>

                          {/* CUSTOMER PAYMENT TAB */}
                          <TabsContent value="customer_payment">
                            <form onSubmit={handleCustomerPaymentSubmit} className="space-y-4 mt-4">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium mb-2">Date *</label>
                                  <Input 
                                    type="date" 
                                    value={customerPaymentForm.date} 
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, date: e.target.value })} 
                                    required 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Customer *</label>
                                  <Select 
                                    value={customerPaymentForm.customer_id} 
                                    onValueChange={(value) => {
                                      setCustomerPaymentForm({ ...customerPaymentForm, customer_id: value });
                                      fetchCustomerBalance(value);
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select customer" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {customers.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.firm_name} - {c.customer_name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Amount (₹) *</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={customerPaymentForm.amount} 
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, amount: e.target.value })} 
                                    required 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Payment Method *</label>
                                  <Select 
                                    value={customerPaymentForm.payment_method} 
                                    onValueChange={(value) => setCustomerPaymentForm({ ...customerPaymentForm, payment_method: value })}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {allPaymentMethods.map(pm => (
                                        <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                                  <Input 
                                    value={customerPaymentForm.description} 
                                    onChange={(e) => setCustomerPaymentForm({ ...customerPaymentForm, description: e.target.value })} 
                                    placeholder="Payment description..."
                                  />
                                </div>
                              </div>

                              {customerBalance && (
                                <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200">
                                  <div className="text-sm space-y-1">
                                    <div className="flex justify-between">
                                      <span className="text-muted">Current Balance:</span>
                                      <span className="font-semibold">₹{customerBalance.current_balance.toLocaleString('en-IN')}</span>
                                    </div>
                                    {getProjectedBalance() !== null && (
                                      <div className="flex justify-between pt-2 border-t">
                                        <span className="font-medium">Projected Balance:</span>
                                        <span className="font-bold text-green-600">₹{getProjectedBalance().toLocaleString('en-IN')}</span>
                                      </div>
                                    )}
                                  </div>
                                </Card>
                              )}

                              <div className="flex gap-2 justify-end pt-4">
                                <Button type="button" variant="outline" onClick={closeTransactionDialog}>Cancel</Button>
                                <Button type="submit">Record Payment</Button>
                              </div>
                            </form>
                          </TabsContent>

                          {/* DAY PAYMENT BREAKDOWN TAB */}
                          <TabsContent value="day_breakdown">
                            <form onSubmit={handlePaymentBreakdownSubmit} className="space-y-4 mt-4">
                              <div className="flex justify-between items-center mb-4">
                                <div className="flex-1">
                                  <label className="block text-sm font-medium mb-2">Date *</label>
                                  <Input 
                                    type="date" 
                                    value={paymentBreakdownForm.date} 
                                    onChange={(e) => {
                                      setPaymentBreakdownForm({ ...paymentBreakdownForm, date: e.target.value });
                                      fetchPaymentBreakdownPrefill(e.target.value);
                                    }} 
                                    required 
                                    className="max-w-xs"
                                  />
                                </div>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => fetchPaymentBreakdownPrefill(paymentBreakdownForm.date)}
                                >
                                  Refresh Prefill
                                </Button>
                              </div>

                              <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm font-medium mb-2">Cash</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.cash} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, cash: e.target.value })} 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Credit Sales <span className="text-xs text-muted">(Auto-filled)</span></label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.credit_sales} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, credit_sales: e.target.value })} 
                                    className="bg-blue-50 dark:bg-blue-900/20"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Credit Collection <span className="text-xs text-muted">(Auto-filled)</span></label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.credit_collection} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, credit_collection: e.target.value })} 
                                    className="bg-blue-50 dark:bg-blue-900/20"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Paytm</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.paytm} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, paytm: e.target.value })} 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">PhonePe</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.phonepe} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, phonepe: e.target.value })} 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">MobiQuick</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.mobiquick} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, mobiquick: e.target.value })} 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Other UPI</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.other_upi} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, other_upi: e.target.value })} 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Card Payments</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.card_payments} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, card_payments: e.target.value })} 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Bank Payments</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.bank_payments} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, bank_payments: e.target.value })} 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Fleet Payments</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.fleet_payments} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, fleet_payments: e.target.value })} 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">OTP Prepayments</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.otp_prepayments} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, otp_prepayments: e.target.value })} 
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-2">Ambulance OTP Credit Sales</label>
                                  <NumericInput 
                                    step="0.01" 
                                    value={paymentBreakdownForm.ambulance_otp_credit_sales} 
                                    onChange={(e) => setPaymentBreakdownForm({ ...paymentBreakdownForm, ambulance_otp_credit_sales: e.target.value })} 
                                  />
                                </div>

                                {/* Custom Payment Methods */}
                                {customPaymentMethods.map(method => (
                                  <div key={method}>
                                    <label className="block text-sm font-medium mb-2">{method}</label>
                                    <NumericInput 
                                      step="0.01" 
                                      value={paymentBreakdownForm.custom_methods[method] || 0} 
                                      onChange={(e) => setPaymentBreakdownForm({ 
                                        ...paymentBreakdownForm, 
                                        custom_methods: {
                                          ...paymentBreakdownForm.custom_methods,
                                          [method]: e.target.value
                                        }
                                      })} 
                                    />
                                  </div>
                                ))}
                              </div>

                              <Card className="p-4 bg-green-50 dark:bg-green-900/20 border-green-200">
                                <div className="text-sm space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="font-medium">Days Total Collection:</span>
                                    <span className="text-lg font-bold text-green-600">₹{calculateDaysTotalCollection().toLocaleString('en-IN')}</span>
                                  </div>
                                  <p className="text-xs text-muted">Note: Days Total will be calculated on server after excluding filtered customer payments</p>
                                </div>
                              </Card>

                              <div className="flex gap-2 justify-end pt-4">
                                <Button type="button" variant="outline" onClick={closeTransactionDialog}>Cancel</Button>
                                <Button type="submit">Record Payment Breakdown</Button>
                              </div>
                            </form>
                          </TabsContent>
                        </Tabs>
                      )}

                      {/* ADD CASH REQUEST FORM (keeping existing code) */}
                      {transactionType === 'cash_request' && (
                        <form onSubmit={handleCashRequestSubmit} className="space-y-4 mt-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">Date *</label>
                              <Input type="date" value={cashRequestForm.date} onChange={(e) => setCashRequestForm({ ...cashRequestForm, date: e.target.value })} required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Customer *</label>
                              <Select value={cashRequestForm.customer_id} onValueChange={(value) => {
                                setCashRequestForm({ ...cashRequestForm, customer_id: value });
                                fetchCustomerBalance(value);
                              }}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select customer" />
                                </SelectTrigger>
                                <SelectContent>
                                  {customers.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.firm_name} - {c.customer_name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Amount (₹) *</label>
                              <NumericInput step="0.01" value={cashRequestForm.amount} onChange={(e) => setCashRequestForm({ ...cashRequestForm, amount: e.target.value })} required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Payment Method *</label>
                              <Select value={cashRequestForm.payment_method} onValueChange={(value) => setCashRequestForm({ ...cashRequestForm, payment_method: value })}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {allPaymentMethods.map(pm => (
                                    <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Customer Employee Name *</label>
                              <Input value={cashRequestForm.customer_employee_name} onChange={(e) => setCashRequestForm({ ...cashRequestForm, customer_employee_name: e.target.value })} required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2">Witness Name *</label>
                              <Input value={cashRequestForm.witness_name} onChange={(e) => setCashRequestForm({ ...cashRequestForm, witness_name: e.target.value })} required />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium mb-2">Note (Optional)</label>
                              <Input value={cashRequestForm.note} onChange={(e) => setCashRequestForm({ ...cashRequestForm, note: e.target.value })} placeholder="Additional notes..." />
                            </div>
                          </div>

                          {customerBalance && (
                            <Card className="p-4 bg-orange-50 dark:bg-orange-900/20 border-orange-200">
                              <div className="text-sm">
                                <div className="flex justify-between mb-1">
                                  <span className="text-muted">Current Balance:</span>
                                  <span className="font-semibold">₹{customerBalance.current_balance.toLocaleString('en-IN')}</span>
                                </div>
                                <p className="text-xs text-muted mt-2">This cash amount will be added to customer's balance</p>
                              </div>
                            </Card>
                          )}
                          
                          <div className="flex gap-2 justify-end pt-4">
                            <Button type="button" variant="outline" onClick={closeTransactionDialog}>Cancel</Button>
                            <Button type="submit">Record Cash Request</Button>
                          </div>
                        </form>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Customer Transactions Table */}
                <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary">
                        <tr>
                          <th className="text-left p-3 text-sm font-semibold">Date</th>
                          <th className="text-left p-3 text-sm font-semibold">Type</th>
                          <th className="text-left p-3 text-sm font-semibold">Customer</th>
                          <th className="text-left p-3 text-sm font-semibold">Details</th>
                          <th className="text-right p-3 text-sm font-semibold">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((txn, idx) => (
                          <tr key={idx} className="border-t border-border hover:bg-secondary/50">
                            <td className="p-3 text-sm">{new Date(txn.date).toLocaleDateString('en-IN')}</td>
                            <td className="p-3">
                              {txn.type === 'sale' && <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">Sale</span>}
                              {txn.type === 'payment' && <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">Payment</span>}
                              {txn.type === 'cash_request' && <span className="px-2 py-1 rounded text-xs bg-orange-100 text-orange-700">Cash Request</span>}
                            </td>
                            <td className="p-3 text-sm">{getCustomerName(txn.customer_id)}</td>
                            <td className="p-3 text-sm text-muted">
                              {txn.type === 'sale' && `${txn.quantity}L ${txn.fuel_type} ${txn.is_credit ? '(Credit)' : '(Paid)'}`}
                              {txn.type === 'payment' && txn.description}
                              {txn.type === 'cash_request' && `Employee: ${txn.customer_employee_name}`}
                            </td>
                            <td className="p-3 text-sm text-right font-semibold">
                              {txn.type === 'sale' && `₹${(txn.net_amount || txn.amount).toLocaleString('en-IN')}`}
                              {txn.type === 'payment' && <span className="text-green-600">₹{txn.amount.toLocaleString('en-IN')}</span>}
                              {txn.type === 'cash_request' && <span className="text-orange-600">₹{txn.amount.toLocaleString('en-IN')}</span>}
                            </td>
                          </tr>
                        ))}
                        {transactions.length === 0 && (
                          <tr>
                            <td colSpan="5" className="p-6 text-center text-muted">No transactions found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </TabsContent>

              {/* PAYMENT BREAKDOWN SUB-TAB */}
              <TabsContent value="payment_breakdown" className="mt-4">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-heading font-bold">Payment Breakdown</h2>
                    <Button onClick={downloadPaymentBreakdown} disabled={!canDownload}>
                      {canDownload ? <Download className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                      {canDownload ? 'Download CSV' : 'Restricted'}
                    </Button>
                  </div>

                  {/* Date Range Filter for Payment Breakdown */}
                  <Card className="p-4">
                    <div className="flex items-center gap-4">
                      <Label>From:</Label>
                      <Input
                        type="date"
                        value={breakdownDateRange.start}
                        onChange={(e) => setBreakdownDateRange({ ...breakdownDateRange, start: e.target.value })}
                        className="w-40"
                      />
                      <Label>To:</Label>
                      <Input
                        type="date"
                        value={breakdownDateRange.end}
                        onChange={(e) => setBreakdownDateRange({ ...breakdownDateRange, end: e.target.value })}
                        className="w-40"
                      />
                      <span className="text-sm text-muted">(Max 30 days)</span>
                    </div>
                  </Card>

                  {/* Payment Breakdown Table */}
                  <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-secondary">
                          <tr>
                            <th className="text-left p-3 font-semibold sticky left-0 bg-secondary">Date</th>
                            <th className="text-right p-3 font-semibold">Cash</th>
                            <th className="text-right p-3 font-semibold">Credit Sales</th>
                            <th className="text-right p-3 font-semibold">Credit Collection</th>
                            <th className="text-right p-3 font-semibold">Paytm</th>
                            <th className="text-right p-3 font-semibold">PhonePe</th>
                            <th className="text-right p-3 font-semibold">MobiQuick</th>
                            <th className="text-right p-3 font-semibold">Other UPI</th>
                            <th className="text-right p-3 font-semibold">Card</th>
                            <th className="text-right p-3 font-semibold">Bank</th>
                            <th className="text-right p-3 font-semibold">Fleet</th>
                            <th className="text-right p-3 font-semibold">OTP Prepay</th>
                            <th className="text-right p-3 font-semibold">Ambulance OTP</th>
                            {customPaymentMethods.map(method => (
                              <th key={method} className="text-right p-3 font-semibold">{method}</th>
                            ))}
                            <th className="text-right p-3 font-semibold bg-green-100 dark:bg-green-900">Days Total Collection</th>
                            <th className="text-right p-3 font-semibold bg-blue-100 dark:bg-blue-900">Days Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentBreakdowns.map((bd, idx) => (
                            <tr key={idx} className="border-t border-border hover:bg-secondary/50">
                              <td className="p-3 sticky left-0 bg-background">{new Date(bd.date).toLocaleDateString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.cash.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.credit_sales.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.credit_collection.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.paytm.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.phonepe.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.mobiquick.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.other_upi.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.card_payments.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.bank_payments.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.fleet_payments.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.otp_prepayments.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right">₹{bd.ambulance_otp_credit_sales.toLocaleString('en-IN')}</td>
                              {customPaymentMethods.map(method => (
                                <td key={method} className="p-3 text-right">₹{(bd.custom_methods?.[method] || 0).toLocaleString('en-IN')}</td>
                              ))}
                              <td className="p-3 text-right font-bold bg-green-50 dark:bg-green-900/20">₹{bd.days_total_collection.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right font-bold bg-blue-50 dark:bg-blue-900/20">₹{bd.days_total.toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                          {paymentBreakdowns.length === 0 && (
                            <tr>
                              <td colSpan="100" className="p-6 text-center text-muted">No payment breakdowns found for this period</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* EXPENSES TAB - Keep existing code */}
          <TabsContent value="expenses" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold">Expenses & Withdrawals</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportExpensePDF} disabled={!canDownload}>
                  {canDownload ? <Download className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  {canDownload ? 'Export PDF' : 'Restricted'}
                </Button>
                {/* Investment/Withdrawal - Only visible to Admin and Manager */}
                {(user?.role === 'admin' || user?.role === 'manager') && (
                <Dialog open={withdrawalDialog} onOpenChange={setWithdrawalDialog}>
                  <DialogTrigger asChild>
                    <Button variant="secondary">
                      <Wallet className="w-4 h-4 mr-2" />
                      Investment/Withdrawal
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Owner Investment or Withdrawal</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleWithdrawalSubmit} className="space-y-4 mt-4">
                      {/* Transaction Type Toggle */}
                      <div className="flex flex-col sm:flex-row gap-2 p-1 bg-secondary rounded-lg">
                        <button
                          type="button"
                          onClick={() => setWithdrawalForm({ ...withdrawalForm, transaction_type: 'investment' })}
                          className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                            withdrawalForm.transaction_type === 'investment' ? 'bg-white shadow text-green-600' : 'text-muted hover:text-foreground'
                          }`}
                        >
                          Investment (Capital In)
                        </button>
                        <button
                          type="button"
                          onClick={() => setWithdrawalForm({ ...withdrawalForm, transaction_type: 'withdrawal' })}
                          className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                            withdrawalForm.transaction_type === 'withdrawal' ? 'bg-white shadow text-red-600' : 'text-muted hover:text-foreground'
                          }`}
                        >
                          Withdrawal (Capital Out)
                        </button>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Date *</label>
                          <Input type="date" value={withdrawalForm.date} onChange={(e) => setWithdrawalForm({ ...withdrawalForm, date: e.target.value })} required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Partner/Owner Name *</label>
                          <Input value={withdrawalForm.partner_name} onChange={(e) => setWithdrawalForm({ ...withdrawalForm, partner_name: e.target.value })} required placeholder="Owner/Partner name" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Amount (₹) *</label>
                          <NumericInput step="0.01" value={withdrawalForm.amount} onChange={(e) => setWithdrawalForm({ ...withdrawalForm, amount: e.target.value })} required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Payment Method *</label>
                          <Select value={withdrawalForm.payment_method} onValueChange={(value) => setWithdrawalForm({ ...withdrawalForm, payment_method: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {allPaymentMethods.map(pm => (
                                <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2">Description</label>
                          <Input value={withdrawalForm.description} onChange={(e) => setWithdrawalForm({ ...withdrawalForm, description: e.target.value })} placeholder={withdrawalForm.transaction_type === 'investment' ? 'Purpose of investment...' : 'Purpose of withdrawal...'} />
                        </div>
                      </div>

                      <Card className={`p-4 ${withdrawalForm.transaction_type === 'investment' ? 'bg-green-50 dark:bg-green-900/20 border-green-200' : 'bg-red-50 dark:bg-red-900/20 border-red-200'}`}>
                        <div className="text-sm">
                          <p className="font-semibold mb-2">
                            {withdrawalForm.transaction_type === 'investment' ? '💰 Investment (Capital In)' : '💸 Withdrawal (Capital Out)'}
                          </p>
                          <p className="text-xs text-muted">
                            {withdrawalForm.transaction_type === 'investment' 
                              ? 'This will increase both Actual Balance (cash added) and Ideal Balance (business value increased)'
                              : 'This will decrease both Actual Balance (cash removed) and Ideal Balance (business value decreased)'}
                          </p>
                        </div>
                      </Card>

                      <div className="flex gap-2 justify-end pt-4">
                        <Button type="button" variant="outline" onClick={() => setWithdrawalDialog(false)}>Cancel</Button>
                        <Button type="submit" className={withdrawalForm.transaction_type === 'investment' ? 'bg-green-600 hover:bg-green-700' : ''}>
                          {withdrawalForm.transaction_type === 'investment' ? 'Record Investment' : 'Record Withdrawal'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                )}
                <Dialog open={expenseDialog} onOpenChange={setExpenseDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Expense
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Expense</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleExpenseSubmit} className="space-y-4 mt-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Date *</label>
                          <Input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Category *</label>
                          <Select value={expenseForm.category} onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {expenseCategories.map(cat => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2">Description *</label>
                          <Input value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Amount (₹) *</label>
                          <NumericInput step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Paid To</label>
                          <Input value={expenseForm.paid_to} onChange={(e) => setExpenseForm({ ...expenseForm, paid_to: e.target.value })} />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium mb-2">Payment Method *</label>
                          <Select value={expenseForm.payment_method} onValueChange={(value) => setExpenseForm({ ...expenseForm, payment_method: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {allPaymentMethods.map(pm => (
                                <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end pt-4">
                        <Button type="button" variant="outline" onClick={() => setExpenseDialog(false)}>Cancel</Button>
                        <Button type="submit">Record Expense</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <div className="grid gap-4 mb-6">
              <Card className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted mb-1">Total Expenses</p>
                    <p className="text-3xl font-bold">₹{expenseStats.total_amount.toLocaleString('en-IN')}</p>
                  </div>
                  <Receipt className="w-12 h-12 text-muted opacity-50" />
                </div>
              </Card>
            </div>

            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold">Date</th>
                      <th className="text-left p-3 text-sm font-semibold">Category</th>
                      <th className="text-left p-3 text-sm font-semibold">Description</th>
                      <th className="text-left p-3 text-sm font-semibold">Paid To</th>
                      <th className="text-right p-3 text-sm font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((exp, idx) => (
                      <tr key={idx} className="border-t border-border hover:bg-secondary/50">
                        <td className="p-3 text-sm">{new Date(exp.date).toLocaleDateString('en-IN')}</td>
                        <td className="p-3 text-sm">{exp.category}</td>
                        <td className="p-3 text-sm">{exp.description}</td>
                        <td className="p-3 text-sm">{exp.paid_to || '-'}</td>
                        <td className="p-3 text-sm text-right font-semibold">₹{exp.amount.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                    {expenses.length === 0 && (
                      <tr>
                        <td colSpan="5" className="p-6 text-center text-muted">No expenses found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Investment/Withdrawal History Section */}
            <div className="mt-8">
              <h3 className="text-xl font-heading font-bold mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Capital Movements History
              </h3>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <Card className="p-4 bg-green-50 dark:bg-green-900/20 border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-400 mb-1">Total Investments</p>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                        ₹{withdrawals.filter(w => w.is_investment || (w.description && w.description.toLowerCase().includes('investment'))).reduce((sum, w) => sum + w.amount, 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
                  </div>
                </Card>
                <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600 dark:text-red-400 mb-1">Total Withdrawals</p>
                      <p className="text-2xl font-bold text-red-700 dark:text-red-300">
                        ₹{withdrawals.filter(w => !w.is_investment && !(w.description && w.description.toLowerCase().includes('investment'))).reduce((sum, w) => sum + w.amount, 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <TrendingDown className="w-8 h-8 text-red-500 opacity-50" />
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted mb-1">Net Capital Flow</p>
                      <p className={`text-2xl font-bold ${
                        (withdrawals.filter(w => w.is_investment || (w.description && w.description.toLowerCase().includes('investment'))).reduce((sum, w) => sum + w.amount, 0) -
                         withdrawals.filter(w => !w.is_investment && !(w.description && w.description.toLowerCase().includes('investment'))).reduce((sum, w) => sum + w.amount, 0)) >= 0 
                          ? 'text-green-600' : 'text-red-600'
                      }`}>
                        ₹{Math.abs(
                          withdrawals.filter(w => w.is_investment || (w.description && w.description.toLowerCase().includes('investment'))).reduce((sum, w) => sum + w.amount, 0) -
                          withdrawals.filter(w => !w.is_investment && !(w.description && w.description.toLowerCase().includes('investment'))).reduce((sum, w) => sum + w.amount, 0)
                        ).toLocaleString('en-IN')}
                        {(withdrawals.filter(w => w.is_investment || (w.description && w.description.toLowerCase().includes('investment'))).reduce((sum, w) => sum + w.amount, 0) -
                          withdrawals.filter(w => !w.is_investment && !(w.description && w.description.toLowerCase().includes('investment'))).reduce((sum, w) => sum + w.amount, 0)) >= 0 
                          ? ' (In)' : ' (Out)'}
                      </p>
                    </div>
                    <Wallet className="w-8 h-8 text-muted opacity-50" />
                  </div>
                </Card>
              </div>

              {/* History Table */}
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="capital-movements-table">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold">Date</th>
                        <th className="text-left p-3 text-sm font-semibold">Type</th>
                        <th className="text-left p-3 text-sm font-semibold">Partner/Owner</th>
                        <th className="text-left p-3 text-sm font-semibold">Description</th>
                        <th className="text-left p-3 text-sm font-semibold">Payment Mode</th>
                        <th className="text-right p-3 text-sm font-semibold">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {withdrawals.map((w, idx) => {
                        const isInvestment = w.is_investment || (w.description && w.description.toLowerCase().includes('investment'));
                        return (
                          <tr key={idx} className="border-t border-border hover:bg-secondary/50">
                            <td className="p-3 text-sm">{new Date(w.date).toLocaleDateString('en-IN')}</td>
                            <td className="p-3 text-sm">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                isInvestment 
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                                {isInvestment ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {isInvestment ? 'Investment' : 'Withdrawal'}
                              </span>
                            </td>
                            <td className="p-3 text-sm font-medium">{w.partner_name}</td>
                            <td className="p-3 text-sm text-muted">{w.description || '-'}</td>
                            <td className="p-3 text-sm capitalize">{w.payment_method?.replace(/_/g, ' ') || 'Cash'}</td>
                            <td className={`p-3 text-sm text-right font-semibold ${isInvestment ? 'text-green-600' : 'text-red-600'}`}>
                              {isInvestment ? '+' : '-'}₹{w.amount.toLocaleString('en-IN')}
                            </td>
                          </tr>
                        );
                      })}
                      {withdrawals.length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-6 text-center text-muted">No capital movements found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

export default QuickActions;
